import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { bookCall, openSopralluogo } from "@/lib/site";

const DEFAULT_MENU = {
  title: "Di cosa hai bisogno?",
  hint: "Così chi ti richiama arriva già preparato. Un tocco e vai alla prenotazione.",
  reasons: [
    "Valutazione gratuita del mio annuncio",
    "Gestione continua — ti seguo ogni giorno",
    "Partenza assistita — si parte da zero",
    "Un servizio specifico (prezzi, annuncio, foto)",
    "Problemi col mio property manager",
    "Altro — ne parliamo a voce",
  ],
};

const MENUS = {
  "aprire-bb": {
    title: "Con chi vuoi parlare?",
    hint: "Massimiliano lavora su richiesta scritta: ti richiama lui dopo aver letto i dati. Per parlare a voce subito c'è Luciano.",
    reasons: [
      "Con Luciano — ho un immobile, voglio capire se i numeri tornano",
      { label: "Con Massimiliano — voglio un sopralluogo di progetto", sopralluogo: "trasformare" },
      { label: "Con Massimiliano — ho già una struttura, voglio rinnovare gli spazi", sopralluogo: "rinnovare" },
      "Con Luciano — voglio tutto il percorso, dai numeri al lancio",
      "Altro — ne parliamo a voce",
    ],
  },
  fotografo: {
    title: "Di cosa hai bisogno?",
    hint: "Per le foto Giovanni ti scrive dopo aver visto il tuo annuncio (modulo in pagina). Per parlare a voce c'è Luciano.",
    reasons: [
      "Foto più annuncio rifatto, con Luciano",
      "Sto aprendo: foto, prezzi e annuncio dal primo giorno",
      "Valutazione gratuita del mio annuncio",
      "Altro — ne parliamo a voce",
    ],
  },
  commercialista: {
    who: "valerio",
    title: "Di cosa hai bisogno, con Valerio?",
    hint: "Così arriva già preparato sulla tua situazione. Un tocco e vai alla prenotazione.",
    reasons: [
      "Sei in regola? 5 domande — gratis",
      "Check-up fiscale della mia struttura",
      "Apertura completa: partita IVA, SCIA, CIN",
      "Cedolare secca o partita IVA: quale mi conviene?",
      "Gestione annuale della contabilità",
      "Altro — ne parliamo a voce",
    ],
  },
  "revenue-manager": {
    title: "Di cosa hai bisogno, con Luciano?",
    hint: "Così arrivo già preparato sulla tua struttura. Un tocco e vai alla prenotazione.",
    reasons: [
      "Valutazione gratuita del mio annuncio",
      "Gestione continua — mi segui ogni giorno",
      "Strategia prezzi per la mia struttura",
      "Ho un property manager, ma i prezzi non funzionano",
      "Sto aprendo: prezzi e annuncio dal primo giorno",
      "Altro — ne parliamo a voce",
    ],
  },
};
MENUS["commercialista-in-regola"] = MENUS.commercialista;

const EASE = [0.16, 1, 0.3, 1];

export default function CallReasonModal() {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("");
  const menu = MENUS[source] || DEFAULT_MENU;

  useEffect(() => {
    const handler = (e) => {
      setSource(typeof e.detail === "string" ? e.detail : "");
      setOpen(true);
    };
    window.addEventListener("open-call-reason", handler);
    return () => window.removeEventListener("open-call-reason", handler);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pick = (reason) => {
    setOpen(false);
    if (typeof reason === "object" && reason.sopralluogo) {
      openSopralluogo(reason.sopralluogo);
      return;
    }
    const label = typeof reason === "object" ? reason.label : reason;
    const who = (typeof reason === "object" && reason.who) || menu.who || "luciano";
    bookCall(label, source, who);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={() => setOpen(false)}
        >
          <motion.div
            data-testid="call-reason-modal"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-[#383029] bg-[#12100E] p-7 sm:p-9"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="section-kicker">Chiamata gratuita · 30 min</p>
                <p className="mt-2 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">
                  {menu.title}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#A89F91]">
                  {menu.hint}
                </p>
              </div>
              <button
                data-testid="call-reason-close"
                onClick={() => setOpen(false)}
                aria-label="Chiudi"
                className="rounded-full border border-[#383029] p-2 text-[#A89F91] transition-colors duration-300 hover:border-[#E05A2C] hover:text-[#FAF7F2]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-7 flex flex-col gap-2.5">
              {menu.reasons.map((reason, i) => {
                const label = typeof reason === "object" ? reason.label : reason;
                return (
                <motion.button
                  key={label}
                  data-testid={`call-reason-option-${i + 1}`}
                  onClick={() => pick(reason)}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 + i * 0.05, duration: 0.4, ease: EASE }}
                  className="group flex items-center justify-between gap-4 border border-[#383029] px-5 py-4 text-left text-sm font-medium text-[#C4BBB0] transition-colors duration-300 hover:border-[#E05A2C] hover:bg-[#1C1815] hover:text-[#FAF7F2]"
                >
                  {label}
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-[#A89F91] transition-colors duration-300 group-hover:text-[#E05A2C]" />
                </motion.button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
